<template>
  <div class="date-range-container">
    <div class="date-inputs-wrapper compact">
      <input
        class="chip-input"
        type="datetime-local"
        :value="utcToLocalInput(startDate)"
        @input="debouncedUpdateStartDate"
        aria-label="Start"
      />
      <span class="sep">–</span>
      <input
        class="chip-input"
        type="datetime-local"
        :value="utcToLocalInput(endDate)"
        @input="debouncedUpdateEndDate"
        aria-label="End"
      />
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, onMounted } from 'vue'
import { 
  utcToLocalInput, 
  localInputToUTC,
  getDurationString,
  getTodayRange
} from '../utils/dateUtils'

const props = defineProps({
  startDate: {
    type: String,
    required: true
  },
  endDate: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['update:startDate', 'update:endDate', 'update'])

const debounce = (fn, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

const debouncedUpdateStartDate = debounce((event) => {
  emit('update:startDate', localInputToUTC(event.target.value))
  emit('update')
}, 1000)

const debouncedUpdateEndDate = debounce((event) => {
  emit('update:endDate', localInputToUTC(event.target.value))
  emit('update')
}, 1000)

const getDuration = () => {
  return getDurationString(props.startDate, props.endDate)
}

onMounted(() => {
  if (!props.startDate || !props.endDate) {
    const range = getTodayRange()
    emit('update:startDate', range.start)
    emit('update:endDate', range.end)
    emit('update')
  }
})
</script>

<style scoped>
.date-range-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
}

.date-inputs-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.date-inputs-wrapper.compact { gap: 0.35rem; justify-content: center; }
.chip-input {
  border: 1px solid var(--border-color);
  border-radius: 999px;
  padding: 0.35rem 0.6rem;
  background: var(--card-background);
  color: var(--text-color);
  font-size: 0.85rem;
  width: auto;
  flex: 1;
  min-width: 0; /* allow shrinking on narrow screens */
}
.sep { color: var(--text-secondary); font-size: 0.85rem; }

.date-input-group {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.date-separator {
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
}

.input-wrapper {
  position: relative;
}

.date-input-group input {
  width: 100%;
  border: 2px solid var(--border-color, #e5e7eb);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  background-color: var(--input-background, #f9fafb);
  color: var(--text-color);
  transition: all 0.2s ease;
}

.date-input-group input:hover {
  border-color: var(--primary-color-light, #60a5fa);
}

.date-input-group input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.date-input-group input::placeholder {
  color: var(--text-secondary);
  opacity: 0.7;
}

@media (max-width: 768px) {
  .date-range-container {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .date-inputs-wrapper {
    flex-direction: row;
    align-items: center;
  }
}

@media (max-width: 480px) {
  .date-inputs-wrapper {
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
    flex-wrap: nowrap; /* keep in one line */
    justify-content: center;
  }

  .date-input-group {
    min-width: 0;
    flex: 1;
    min-width: 120px;
    max-width: calc(50% - 0.125rem);
  }

  .date-input-group input {
    font-size: 0.8rem;
    padding: 0.35rem 0.45rem;
  }

  .chip-input {
    font-size: 0.8rem;
    padding: 0.35rem 0.5rem;
  }

  .date-separator {
    display: none;
  }

  .date-range-container {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
}

@media (max-width: 360px) {
  .date-inputs-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .date-separator {
    display: none;
  }

  .date-input-group {
    min-width: auto;
  }
}
</style> 
